import { useState, useCallback } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { fetchJson } from '@atlassian/jira-fetch';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
	X2C_SERVICE_URL_PREFIX,
	isSynthetic,
} from '@atlassian/jira-project-create-importer-common';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { APP_IDENTIFIER } from '../../common/constants';
import { EXPERIENCES, validateMappingInteractionMetric } from '../../common/constants/metrics';
import type { Source } from '../../common/constants/source';
import type { MigrationId, X2CImportValidationError } from '../../common/types';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useGetCSVImportClient } from '../../common/utils/csv-import-clients';
import type { ValidateImportPayload } from '../../common/utils/csv-import-clients/types';
import { transformToValidateImportPayload } from '../../common/utils/csv-import-clients/utils';
import type { UseValidateX2CImport, ValidateX2CImportArgs } from './types';

const getX2CImportUrl = (cloudId: string, migrationId: MigrationId) =>
	`${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/csv/${migrationId}/validate`;

export const validateX2CImportRequest = (
	cloudId: string,
	migrationId: MigrationId,
	payload: ValidateImportPayload,
): Promise<X2CImportValidationError> =>
	fetchJson(getX2CImportUrl(cloudId, migrationId), {
		body: JSON.stringify(payload),
		method: 'POST',
		headers: {
			'x-issynthetic-traffic': String(isSynthetic()),
		},
	});

export const useValidateX2CImport = (
	source: Source,
	serviceName?: string,
): UseValidateX2CImport => {
	const cloudId = useCloudId();
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.VALIDATE,
	);
	const [isLoading, setIsLoading] = useState(false);
	const additionalAttributes = useAnalyticsAttributesContext();
	const client = useGetCSVImportClient(source, serviceName);

	const validateX2CImport = useCallback(
		async ({
			migrationId,
			mappingState,
			advancedOptions,
			onSuccess,
			onFetchFail,
			onValidationError,
		}: ValidateX2CImportArgs) => {
			setIsLoading(true);
			const payload = transformToValidateImportPayload(
				mappingState,
				advancedOptions,
				ff('x2c-issuelinking-frontend_tuyzk'),
			);
			let validationResponse: X2CImportValidationError;

			validateMappingInteractionMetric.start();
			onExperienceStart();
			try {
				if (ff('enable-importing-data-to-existing-project_k8uub')) {
					validationResponse = await client.validateRequest({
						migrationId,
						mappingState,
						advancedOptions,
					});
				} else {
					validationResponse = await validateX2CImportRequest(cloudId, migrationId, payload);
				}
				validateMappingInteractionMetric.stop();
				onExperienceSuccess({ migrationId, ...additionalAttributes });
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				validateMappingInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, { migrationId, ...additionalAttributes });
				fireErrorAnalytics({
					meta: {
						id: 'validate-x2c-import-error',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					attributes: {
						message: 'Failed to validate payload for jwm import',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				setIsLoading(false);
				onFetchFail();
				return;
			}
			setIsLoading(false);
			if (
				Object.keys(validationResponse?.columnErrors ?? {}).length > 0 ||
				validationResponse?.missingFields?.length > 0
			) {
				onValidationError(validationResponse);
				return;
			}
			onSuccess();
		},
		[
			onExperienceStart,
			onExperienceSuccess,
			additionalAttributes,
			client,
			cloudId,
			onExperienceFail,
		],
	);

	return {
		isLoading,
		validateX2CImport,
	};
};
